<script setup>
import VueApexCharts from 'vue3-apexcharts';

const props = defineProps({
  // 차트에 표시할 시리즈 데이터 [첫번째, 두번째]
  series: {
    type: Array,
    default: () => [0, 0],
  },
  // 중앙 라벨 (예: 양실음허, 한습)
  centerLabel: {
    type: String,
    default: '',
  },
  // 퍼센트 뱃지 (예: 77.2%)
  percent: {
    type: String,
    default: '',
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  // 차트 타입: 'yinyang' | 'johu'
  chartType: {
    type: String,
    default: 'yinyang',
  },
});

// 타입별 색상 및 라벨 설정
const colorConfigs = {
  yinyang: {
    colors: ['#727272', '#EAEAEA'],
    labels: ['음의 기운', '양의 기운'],
  },
  johu: {
    colors: ['#F5C242', '#5B9BD5'],
    labels: ['뜨거운 기운', '차가운 기운'],
  },
};

const currentConfig = computed(() => colorConfigs[props.chartType] || colorConfigs.yinyang);

const chartSize = computed(() => (props.isMobile ? 150 : 200));

const chartOptions = computed(() => ({
  chart: {
    type: 'donut',
    height: chartSize.value,
    width: chartSize.value,
    parentHeightOffset: 0,
  },
  labels: currentConfig.value.labels,
  colors: currentConfig.value.colors,
  stroke: {
    width: 0,
  },
  legend: {
    show: false,
  },
  plotOptions: {
    pie: {
      donut: {
        size: '50%',
        labels: {
          show: false,
        },
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    enabled: true,
    custom: ({ series, seriesIndex, w }) => {
      const label = w.config.labels[seriesIndex];
      const value = Number(series[seriesIndex]).toFixed(1);
      const bgColor = w.config.colors[seriesIndex];
      // 밝은 색상일 때 텍스트를 #333으로
      const lightColors = ['#EAEAEA', '#F5C242'];
      const textColor = lightColors.includes(bgColor) ? '#333' : '#fff';
      return `<div style="background:${bgColor};padding:8px 12px;border-radius:4px;color:${textColor};font-size:13px;">${label}: ${value}%</div>`;
    },
  },
}));
</script>

<template>
  <div class="yin-yang-chart">
    <div class="yin-yang-chart__donut-wrapper">
      <VueApexCharts
        v-if="series.length > 0"
        :options="chartOptions"
        :series="series"
        type="donut"
        :height="chartSize"
        :width="chartSize"
      />
      <div class="yin-yang-chart__center-label">{{ centerLabel }}</div>
      <div v-if="percent" class="yin-yang-chart__percent-badge" :class="{ mobile: isMobile }">
        {{ percent }}
      </div>
    </div>
    <div class="yin-yang-chart__legend">
      <div class="yin-yang-chart__legend-item">
        <span
          class="yin-yang-chart__legend-dot"
          :style="{ backgroundColor: currentConfig.colors[0] }"
        ></span>
        <span class="yin-yang-chart__legend-text">{{ currentConfig.labels[0] }}</span>
      </div>
      <div class="yin-yang-chart__legend-item">
        <span
          class="yin-yang-chart__legend-dot"
          :style="{ backgroundColor: currentConfig.colors[1] }"
        ></span>
        <span class="yin-yang-chart__legend-text">{{ currentConfig.labels[1] }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.yin-yang-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  &__donut-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__center-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -55%);
    color: #333;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
    pointer-events: none;
  }

  &__percent-badge {
    position: absolute;
    bottom: 24%;
    right: 5%;
    padding: 4px 8px;
    border-radius: 30px;
    background: #333;
    color: #fff;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;

    &.mobile {
      bottom: 18%;
      right: 2%;
    }
  }

  &__legend {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }

  &__legend-text {
    color: #333;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
  }
}
</style>
